import React from "react";
import styles from "./ChatInterface.module.css";
import { CHAT_MESSAGE_MAX_CHARS, MESSAGE_TOO_LONG_COPY, isMessageTooLong, messageLength } from "./messageLimit";

/**
 * TRAC-497: the floating-island message input (textarea + send button) extracted
 * from ChatInterface. Pure presentational — the parent owns inputValue and all
 * the send/keyboard/YMMV handlers and passes them in already gated on ymmvMode.
 * Shares ChatInterface.module.css so styling is byte-identical. Hidden during the
 * YMMV tire-size step (step 2 has its own UI).
 */

interface ChatInputBarProps {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  inputValue: string;
  onChangeText: (value: string) => void;
  onKeyPress?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  onSend: () => void;

  ymmvMode: boolean;
  ymmvStep: "vehicle" | "tire_size";
  emergencyActive: boolean;
  isDemoMode: boolean;
  highlightInput: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  pendingSelection: boolean;
  retryAttempt: number | null;

  // TRAC-732: during a live-human handoff the input talks to a PERSON, not the AI —
  // so the placeholder should say "Message {rep}…" instead of "Ask me about tires…".
  handoffActive: boolean;
  handoffAgentName: string;

  // TRAC-959: while the conversation is empty the composer stands three rows
  // tall on desktop. TRAC-1124: one row on a phone. The CSS min-height of
  // .messageInputPreConversation is the one source for both (ChatInterface's
  // auto-resize reads it back), so the loading skeleton, which renders this
  // component without that effect, lands at the identical height.
  preConversation?: boolean;
}

const ChatInputBar: React.FC<ChatInputBarProps> = ({
  inputRef,
  inputValue,
  onChangeText,
  onKeyPress,
  onKeyDown,
  onSend,
  ymmvMode,
  ymmvStep,
  emergencyActive,
  isDemoMode,
  highlightInput,
  isLoading,
  isStreaming,
  pendingSelection,
  retryAttempt,
  handoffActive,
  handoffAgentName,
  preConversation = false,
}) => {
  const tooLong = isMessageTooLong(inputValue);
  return (
    <div
      className={`${styles.inputArea} ${styles.embedInputArea} ${ymmvMode ? styles.ymmvInputArea : ""} ${ymmvStep === "tire_size" && ymmvMode ? styles.hidden : ""}`}
    >
      <div className={styles.inputWrapper}>
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => onChangeText(e.target.value)}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          placeholder={
            emergencyActive
              ? "Chat is temporarily unavailable"
              : isDemoMode
                ? "Demo mode - chat is read-only"
                : ymmvMode
                  ? "Enter your vehicle (e.g., 2023 Toyota Camry)"
                  : handoffActive
                    ? `Message ${handoffAgentName}…`
                    : "Ask me about tires..."
          }
          className={`${styles.messageInput} ${
            highlightInput ? styles.messageInputHighlighted : ""
          }${preConversation ? ` ${styles.messageInputPreConversation}` : ""}`}
          rows={1}
          disabled={isDemoMode || emergencyActive}
          readOnly={isDemoMode || emergencyActive}
          aria-invalid={tooLong || undefined}
        />
        {!ymmvMode && (
          <button
            onClick={onSend}
            disabled={
              isDemoMode ||
              emergencyActive ||
              !inputValue.trim() ||
              tooLong ||
              isLoading ||
              isStreaming ||
              pendingSelection ||
              retryAttempt !== null
            }
            className={styles.sendButton}
          >
            {isLoading || isStreaming || pendingSelection ? (
              <div className={styles.spinner}></div>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor" />
              </svg>
            )}
          </button>
        )}
      </div>
      {tooLong && !ymmvMode && (
        <div role="alert" data-testid="message-too-long" className={styles.messageTooLong}>
          {MESSAGE_TOO_LONG_COPY} ({messageLength(inputValue).toLocaleString()} of {CHAT_MESSAGE_MAX_CHARS.toLocaleString()})
        </div>
      )}
    </div>
  );
};

export default ChatInputBar;
